import type { TypeKey } from "../ids"
import type { DoneCheck } from "./item"

// #region item-types
/**
 * The built-in item type registry (decision 0035): a type is a label with defaults, never extra fields. No editor this
 * release; a later workspace registry replaces the constant. `Item.type` must be a key here (decide: Invalid).
 * P2, after this release: scaffolds, status labels, a default preset, groups, and the content/post/collect/review types
 * (additive keys or members).
 */
export interface TypeDef {
  readonly key: TypeKey
  readonly label: string
  /**
   * Plannable types are the planning layer: at the top level they show Later / Next / Now, count against WIP when Now,
   * have no assignee (their children do the work) and are never dispatched themselves (readiness `empty` until they
   * have children).
   */
  readonly plannable: boolean
  readonly kind: "work" | "publish"
  /** Written into CreateItem when it names no checks. */
  readonly defaultChecks: ReadonlyArray<DoneCheck>
}

const type = (
  key: string,
  label: string,
  plannable: boolean,
  defaultChecks: ReadonlyArray<DoneCheck> = [],
): TypeDef => ({ key: key as TypeKey, label, plannable, kind: "work", defaultChecks })

export const ITEM_TYPES: ReadonlyArray<TypeDef> = [
  type("release", "Release", true),
  type("bet", "Bet", true),
  type("workstream", "Workstream", true),
  type("task", "Task", false),
  // the owner writes a feature's checks from its acceptance criteria
  type("feature", "Feature", false),
  type("bug", "Bug", false, [
    { _tag: "rubric", criteria: ["a test reproduces the bug before the fix and passes after it"] },
  ]),
  type("research", "Research", false, [
    { _tag: "artifact", kind: "report", description: "the findings, with sources" },
  ]),
  type("chore", "Chore", false),
  type("idea", "Idea", false),
  type("question", "Question", false),
]

/** The default type of a new item. */
export const DEFAULT_ITEM_TYPE = "task" as TypeKey

export const typeDefOf = (key: string): TypeDef | undefined => ITEM_TYPES.find((t) => t.key === key)

/** Is a type key plannable? Unknown keys are not. */
export const isPlannableType = (key: string): boolean => typeDefOf(key)?.plannable === true
// #endregion item-types
